import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';

import { api, ApiError, type Plan, type PlanFeatures } from '../lib/api.js';
import { loadStripe, type StripeElements } from '../lib/stripe.js';
import { Icon, type IconName } from './Icon.js';

/**
 * The commercial matrix and the card form, shared by the two places a producer
 * can buy: the Plan & billing tab of an existing production, and step 2 of the
 * new-project wizard.
 *
 * ONE CARD FORM, DELIBERATELY. A second Stripe Element mounted somewhere else
 * is a second place for the publishable key, the confirmation call, the error
 * copy and the PCI boundary to drift — and payment is the one surface where
 * drift is expensive. Both callers mount THIS one.
 */
/** The matrix, in the document's own order and wording. */
export const FEATURE_ROWS: { key: keyof PlanFeatures; label: string }[] = [
  { key: 'locationManagement', label: 'Location management tool (folders, history, templates)' },
  { key: 'aiScriptLimit', label: 'AI scout' },
  { key: 'marketInsights', label: 'Market insights and data' },
  { key: 'mediaStorage', label: 'Media & file storage' },
  { key: 'exclusiveLocations', label: 'Access to exclusive locations' },
  { key: 'collaboration', label: 'Collaboration module (unlimited users)' },
  { key: 'scheduling', label: 'Scheduling module' },
  { key: 'fastEquipment', label: 'Fast equipment delivery' },
  { key: 'personalizedSupport', label: 'Personalized support' },
];

/**
 * THE ONE MONEY FORMATTER. Every amount on screen is integer minor units the
 * server sent, printed here and nowhere else; nothing calls it with a figure the
 * browser worked out.
 *
 * `cents` keeps the two decimals on a whole amount — a balance or a ledger line
 * reads "€50.00", where a price reads "€50". `signed` prints "+" and "-" for a
 * ledger line that adds or takes away.
 */
export function money(
  minor: number,
  currency: string,
  options: { cents?: boolean; signed?: boolean } = {},
): string {
  return new Intl.NumberFormat('en-IE', {
    style: 'currency',
    currency,
    // Whole euro for a whole amount — "€50" reads as a price, "€50.00" reads
    // as a line on an invoice.
    minimumFractionDigits: options.cents === true || minor % 100 !== 0 ? 2 : 0,
    ...(options.signed === true ? { signDisplay: 'exceptZero' as const } : {}),
  }).format(minor / 100);
}

/**
 * STRIPE'S PAYMENT ELEMENT, MOUNTED ONCE, FOR EVERY PURCHASE IN THE WORKSPACE.
 *
 * A plan and a credit package are bought through this one hook, so the
 * publishable key, the mount, the confirmation call and its error copy live in
 * one place (the reason `PaymentPanel` exists, now shared with
 * `components/credits/CreditPackagePicker.tsx`). The card number never touches
 * this application: the Element is an iframe served by Stripe.
 *
 * `confirm()` resolves when Stripe accepted the payment and rejects with the
 * sentence to show when it did not. Accepted is NOT paid: what was bought is
 * granted by the webhook, and each caller waits for that on its own answer.
 */
export function useStripePaymentElement(
  clientSecret: string | null | undefined,
  publishableKey: string | null | undefined,
) {
  const mountPoint = useRef<HTMLDivElement | null>(null);
  const elementsRef = useRef<StripeElements | null>(null);
  const stripeRef = useRef<Awaited<ReturnType<typeof loadStripe>> | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    if (!clientSecret || !publishableKey || !mountPoint.current) return;

    let cancelled = false;

    void loadStripe(publishableKey)
      .then((stripe) => {
        if (cancelled || !mountPoint.current) return;

        stripeRef.current = stripe;
        const elements = stripe.elements({ clientSecret });
        elementsRef.current = elements;
        elements.create('payment').mount(mountPoint.current);
      })
      .catch(() => {
        if (!cancelled) {
          setLoadError(
            'The payment form could not load. An ad blocker or privacy extension is the usual cause.',
          );
        }
      });

    return () => {
      cancelled = true;
      elementsRef.current?.getElement('payment')?.unmount();
    };
  }, [clientSecret, publishableKey]);

  const confirm = async (): Promise<void> => {
    const stripe = stripeRef.current;
    const elements = elementsRef.current;
    if (!stripe || !elements) throw new Error('The payment form is not ready yet');

    const result = await stripe.confirmPayment({
      elements,
      // Stays on the page (a 3-D Secure card challenge is a dialog here) unless
      // the producer picks a redirect-based payment method: Stripe then returns
      // to return_url with the client secret in the query, and main.tsx removes
      // it before the router starts (#1732).
      redirect: 'if_required',
      confirmParams: { return_url: window.location.href },
    });

    if (result.error) throw new Error(result.error.message ?? 'That payment was declined');
  };

  return { mountPoint, confirm, loadError };
}

/** What a caller's own "the plan is active" view is handed. */
export interface ActivePlan {
  planCode: string;
  /** The end of the paid period, already written out ("5 May 2027"), or null. */
  until: string | null;
  /** The panel's `onPaid`: the caller's "we are done here". */
  onContinue: () => void;
}

/**
 * The card form.
 *
 * Stripe's own Payment Element, mounted into a div we own. The card number
 * never touches this application — it is an iframe served by Stripe, which is
 * what keeps the workspace out of PCI scope.
 *
 * THE CALLER MAY DRAW THE TWO ENDS, never the middle. `lead` is a sentence
 * above the card form, dropped once the card is charged because it is about
 * paying; `renderActive` replaces the default "plan is active" view, which the
 * new-project page draws as the production's arrival. What happens between —
 * the charge, the wait for the webhook, the slow path — stays here, the same
 * for every caller.
 */
export function PaymentPanel({
  projectId,
  planCode,
  termMonths,
  onCancel,
  onPaid,
  lead,
  renderActive,
}: {
  projectId: string;
  planCode: string;
  termMonths: number;
  onCancel: () => void;
  onPaid: () => Promise<void>;
  lead?: ReactNode;
  renderActive?: (plan: ActivePlan) => ReactNode;
}) {
  const [error, setError] = useState<string | null>(null);
  const [paying, setPaying] = useState(false);

  /**
   * The money has moved, and the plan has not arrived yet.
   *
   * THE DEFECT THIS FIXES: the panel used to call `onPaid()` the instant Stripe
   * accepted the card, which invalidated the entitlement query and closed the
   * panel. But the entitlement is granted by the WEBHOOK, which lands a moment
   * later — so the refetch read the OLD state, the panel vanished, and the
   * producer was returned to a screen that still said Free, with no message of
   * any kind. They had paid and nothing appeared to happen.
   *
   * Two facts, and they become true at different times: "your card was
   * charged" is known here, immediately, and is worth saying at once; "your
   * plan is active" is known only when the entitlement flips. The panel now
   * says the first while it waits for the second.
   */
  const [paid, setPaid] = useState(false);
  const [waitedTooLong, setWaitedTooLong] = useState(false);

  const start = useQuery({
    queryKey: ['purchase', projectId, planCode, termMonths],
    queryFn: () => api.startPurchase(projectId, planCode, termMonths),
    // A purchase intent is a side effect, so it must not be retried or refetched
    // behind the producer's back.
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });

  // The Element and its confirmation call, shared with the credit purchase.
  const card = useStripePaymentElement(start.data?.clientSecret, start.data?.publishableKey);

  const pay = useMutation({
    mutationFn: () => card.confirm(),
    onMutate: () => {
      setPaying(true);
      setError(null);
    },
    onSuccess: () => {
      // NOT `onPaid()` — that closes this panel, and closing it here is what
      // made a successful payment look like nothing happening. The card is
      // charged; now we wait for the webhook to grant the period, and say so.
      setPaying(false);
      setPaid(true);
    },
    onError: (e: unknown) => {
      setPaying(false);
      setError(e instanceof Error ? e.message : 'That payment could not be completed');
    },
  });

  /**
   * Watch the entitlement until it flips.
   *
   * Polled rather than assumed: only the entitlement can say the plan is
   * active, because only the webhook grants it. A UI that declared success on
   * Stripe's word alone would be right almost always and wrong exactly when the
   * webhook is broken — which is the case where a producer most needs the
   * truth.
   */
  const entitlement = useQuery({
    queryKey: ['billing', projectId],
    queryFn: () => api.billingState(projectId),
    enabled: paid,
    refetchInterval: (query) => (paid && query.state.data?.state !== 'PAID' ? 2000 : false),
  });

  const active = paid && entitlement.data?.state === 'PAID';

  useEffect(() => {
    if (!paid || active) return;

    // A BOUND, for the same reason the screenplay poll has one: a wait with no
    // end is a promise the screen cannot keep. Twenty seconds is far longer
    // than a healthy webhook takes and short enough that nobody sits wondering.
    const timer = setTimeout(() => {
      setWaitedTooLong(true);
    }, 20_000);

    return () => {
      clearTimeout(timer);
    };
  }, [paid, active]);

  const failure = start.error;

  // ONCE PAID, THE CARD FORM IS GONE. Leaving a "Pay now" button on screen
  // after a successful charge invites a second one.
  if (paid) {
    const ends = entitlement.data?.periodEndsAt;
    const until =
      ends === null || ends === undefined
        ? null
        : new Date(ends).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });

    if (active && renderActive !== undefined) {
      return (
        <div data-testid="payment-panel">
          {renderActive({
            planCode: entitlement.data?.planCode ?? planCode,
            until,
            onContinue: () => {
              void onPaid();
            },
          })}
        </div>
      );
    }

    return (
      <div className="card1" style={{ padding: 20, marginTop: 18 }} data-testid="payment-panel">
        {active ? (
          <div data-testid="payment-success">
            <p className="dh" style={{ fontSize: 20, margin: 0 }}>
              You are on {entitlement.data?.planCode ?? planCode}
            </p>
            <p className="mm" style={{ fontSize: 13, margin: '8px 0 0', lineHeight: 1.6 }}>
              Payment received and the plan is active
              {until === null ? '' : ` until ${until}`}. Everything the plan includes is available
              now — no need to reload.
            </p>

            <button
              type="button"
              className="btn btn-primary"
              style={{ marginTop: 16 }}
              data-testid="payment-done"
              onClick={() => {
                void onPaid();
              }}
            >
              Continue
            </button>
          </div>
        ) : waitedTooLong ? (
          // NOT AN ERROR, AND IT MUST NOT READ AS ONE. Their card was charged;
          // what has not happened is on our side. Saying "payment failed" here
          // would be false and would send them to pay again.
          <div data-testid="payment-slow">
            <p className="dh" style={{ fontSize: 18, margin: 0 }}>
              Payment received — the plan is taking longer than usual to activate
            </p>
            <p className="mm" style={{ fontSize: 13, margin: '8px 0 0', lineHeight: 1.6 }}>
              Your card was charged and nothing is lost. The plan activates from our side and has
              not come through yet. Do not pay again — check back in a minute, and if it is still
              not active, tell us and we will settle it by hand.
            </p>

            <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
              <button
                type="button"
                className="ctl"
                data-testid="payment-recheck"
                onClick={() => {
                  setWaitedTooLong(false);
                  void entitlement.refetch();
                }}
              >
                Check again
              </button>
              <button
                type="button"
                className="ctl"
                data-testid="payment-done"
                onClick={() => {
                  void onPaid();
                }}
              >
                Continue anyway
              </button>
            </div>
          </div>
        ) : (
          <div data-testid="payment-activating">
            <p className="dh" style={{ fontSize: 18, margin: 0 }}>
              Payment received
            </p>
            <p
              className="mm"
              style={{ fontSize: 13, margin: '8px 0 0', lineHeight: 1.6 }}
              role="status"
              aria-live="polite"
            >
              Your card was charged. Activating the plan…
            </p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="card1" style={{ padding: 20, marginTop: 18 }} data-testid="payment-panel">
      {lead}
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
        <p className="colh" style={{ margin: 0 }}>
          Pay for {planCode} · {termMonths} month{termMonths === 1 ? '' : 's'}
        </p>
        {start.data ? (
          <span style={{ fontSize: 13, fontWeight: 700 }} data-testid="payment-amount">
            {money(start.data.amountMinor, start.data.currency)}
          </span>
        ) : null}
        <button
          type="button"
          className="ctl"
          style={{ marginLeft: 'auto' }}
          onClick={onCancel}
          data-testid="billing-payment-cancel-btn"
        >
          Cancel
        </button>
      </div>

      {failure ? (
        <p
          className="mm"
          style={{ fontSize: 12.5, marginTop: 12, color: 'var(--danger)' }}
          data-testid="payment-start-error"
        >
          {failure instanceof ApiError ? failure.message : 'Could not start that purchase.'}
        </p>
      ) : null}

      <div ref={card.mountPoint} style={{ marginTop: 16 }} data-testid="stripe-element" />

      {(error ?? card.loadError) !== null ? (
        <p
          className="mm"
          style={{ fontSize: 12.5, marginTop: 12, color: 'var(--danger)' }}
          data-testid="payment-error"
        >
          {error ?? card.loadError}
        </p>
      ) : null}

      <button
        type="button"
        className="btn btn-primary"
        style={{ marginTop: 16 }}
        disabled={paying || !start.data?.clientSecret}
        data-testid="payment-submit"
        onClick={() => {
          pay.mutate();
        }}
      >
        {paying ? 'Paying…' : 'Pay now'}
      </button>
    </div>
  );
}

/** Where the design lands when a paid plan is picked — the middle term, not the
 *  cheapest one. Three months is roughly the length of a production; six is a
 *  commitment somebody can make on purpose, having seen it. */
function defaultPrice(plan: Plan): Plan['prices'][number] | null {
  return plan.prices.find((p) => p.termMonths === 3) ?? plan.prices[0] ?? null;
}

/** The cheapest month on a plan, which is what "from €35 / month" means. */
function bestPrice(plan: Plan): Plan['prices'][number] | null {
  return plan.prices.reduce<Plan['prices'][number] | null>(
    (best, price) =>
      best === null || price.monthlyEquivalentMinor < best.monthlyEquivalentMinor ? price : best,
    null,
  );
}

/** The saving against paying month by month — the reason the longer terms exist. */
function savingPercent(plan: Plan, termMonths: number): number | null {
  const monthly = plan.prices.find((p) => p.termMonths === 1);
  const price = plan.prices.find((p) => p.termMonths === termMonths);

  if (!monthly || !price || monthly.amountMinor === 0 || termMonths === 1) return null;

  const saved = Math.round((1 - price.monthlyEquivalentMinor / monthly.amountMinor) * 100);

  return saved > 0 ? saved : null;
}

export function termLabel(termMonths: number): string {
  return termMonths === 1 ? 'Monthly' : `${String(termMonths)} months`;
}

/** The AI row once AI is metered: on every plan, paid per use, never included (owner ruling E1). */
export const AI_PER_USE_LABEL = 'AI scout, paid per use from project credits';

function hasFeature(plan: Plan, key: keyof PlanFeatures): boolean {
  if (key === 'aiScriptLimit' && plan.aiPerUse === true) return true;
  const value = plan.features[key];

  return typeof value === 'number' ? value > 0 : value;
}

/** A row's label on this plan's card: the AI row says how AI is paid once it is metered. */
function rowLabel(plan: Plan, row: { key: keyof PlanFeatures; label: string }): string {
  return row.key === 'aiScriptLimit' && plan.aiPerUse === true ? AI_PER_USE_LABEL : row.label;
}

/** What this tier adds over the one before it, and what it still does not have. */
function featureSplit(plan: Plan, previous: Plan | null) {
  return {
    added: FEATURE_ROWS.filter(
      (row) => hasFeature(plan, row.key) && (previous === null || !hasFeature(previous, row.key)),
    ),
    missing: FEATURE_ROWS.filter((row) => !hasFeature(plan, row.key)),
  };
}

/**
 * THE COMMERCIAL COMPARISON, ONCE.
 *
 * Two screens sell the same three tiers: step 2 of creating a production, and
 * Plan & billing on one that exists. They were different screens showing
 * different things — the creation step had the design's comparison, the
 * billing tab had a stack of cards from before it — which meant the prices a
 * producer agreed to and the prices they saw afterwards were rendered by two
 * pieces of code with no reason to agree.
 *
 * Everything priced here is read from the catalogue the API returns. Nothing
 * about a tier, a term or a saving is written down in this file.
 */
export function PlanChooser({
  plans,
  chosen,
  onChoose,
  currentPlanCode = null,
}: {
  plans: readonly Plan[];
  /** `null` is Free — the tier with no purchase to make. */
  chosen: { code: string; term: number } | null;
  onChoose: (next: { code: string; term: number } | null) => void;
  /** Badged as the plan in force. Absent while creating, since there is none. */
  currentPlanCode?: string | null;
}) {
  const selectedPlan = plans.find((p) => p.code === chosen?.code) ?? null;

  return (
    <>
    {/* FREE IS A REAL CHOICE, and it is first so nobody has to hunt for
        it. A producer who wants to try the product must not have to
        decline a purchase to do so. */}
    <div className="npg-plans" style={{ marginTop: 20 }}>
      {plans.map((plan, i) => {
        const on = plan.isFree ? chosen === null : chosen?.code === plan.code;
        const previous = i === 0 ? null : (plans[i - 1] ?? null);
        const { added, missing } = featureSplit(plan, plan.isFree ? null : previous);
        const from = bestPrice(plan);

        return (
          <button
            key={plan.code}
            type="button"
            data-testid={`plan-${plan.code}`}
            data-on={on ? '1' : '0'}
            aria-pressed={on}
            onClick={() => {
              // A free plan has a price row of 0 for one month; choosing
              // it means buying nothing, so it is recorded as no purchase
              // rather than as a €0 charge Stripe would refuse anyway.
              onChoose(
                plan.isFree
                  ? null
                  : { code: plan.code, term: defaultPrice(plan)?.termMonths ?? 1 },
              );
            }}
            style={{
              position: 'relative',
              textAlign: 'left',
              cursor: 'pointer',
              padding: '22px 22px 24px',
              background: on ? 'var(--color-surface)' : 'var(--color-bg)',
              border: `1px solid ${on ? 'var(--color-accent)' : 'var(--color-divider)'}`,
              boxShadow: on ? 'inset 0 0 0 1px var(--color-accent)' : 'none',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <span
              aria-hidden="true"
              style={{
                position: 'absolute',
                top: 24,
                right: 22,
                width: 18,
                height: 18,
                borderRadius: '50%',
                border: `1px solid ${on ? 'var(--color-accent)' : 'var(--color-divider)'}`,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {on ? (
                <span
                  style={{
                    width: 9,
                    height: 9,
                    borderRadius: '50%',
                    background: 'var(--color-accent)',
                  }}
                />
              ) : null}
            </span>

            <span
              style={{
                display: 'flex',
                alignItems: 'baseline',
                gap: 10,
                flexWrap: 'wrap',
                paddingRight: 34,
              }}
            >
              <span className="dh" style={{ fontSize: 22 }}>
                {plan.name}
              </span>
              {/* WHICH ONE IS IN FORCE. On the billing tab a producer reads a
                  catalogue they have already bought from, and without this the
                  page gives no sign of which row is theirs. Absent while
                  creating, where there is none. */}
              {currentPlanCode === plan.code ? (
                <span className="chip" data-testid={`plan-${plan.code}-current`}>
                  current plan
                </span>
              ) : null}
            </span>
            {plan.description === null ? null : (
              <span
                className="mm"
                style={{ display: 'block', fontSize: 12.5, marginTop: 8, lineHeight: 1.55 }}
              >
                {plan.description}
              </span>
            )}

            <span style={{ display: 'block', marginTop: 18 }}>
              {plan.isFree || from === null ? (
                <>
                  <span className="dh" style={{ fontSize: 30 }}>
                    {money(0, 'EUR')}
                  </span>
                  <span
                    className="mm"
                    style={{ display: 'block', fontSize: 12.5, marginTop: 6 }}
                  >
                    per project
                  </span>
                </>
              ) : (
                <>
                  <span style={{ fontSize: 22, fontWeight: 700 }}>
                    from {money(from.monthlyEquivalentMinor, from.currency)}
                    <span style={{ fontWeight: 400 }}> / month</span>
                  </span>
                  {/* THE REAL TERM, not "billed annually": the catalogue
                      has no annual price, and printing one would be a
                      commitment the product cannot sell. */}
                  <span
                    className="mm"
                    style={{ display: 'block', fontSize: 12.5, marginTop: 6 }}
                  >
                    {`Billed for ${termLabel(from.termMonths).toLowerCase()} (${money(from.amountMinor, from.currency)})`}
                  </span>
                </>
              )}
            </span>

            <span
              aria-hidden="true"
              style={{
                display: 'block',
                height: 1,
                background: 'var(--color-divider)',
                margin: '18px 0',
              }}
            />

            <span style={{ display: 'grid', gap: 7 }}>
              {previous !== null && !plan.isFree ? (
                <span style={{ display: 'flex', gap: 9, fontSize: 12.5, fontWeight: 600 }}>
                  <Icon name="check" size={14} strokeWidth={2.2} />
                  {`Everything in ${previous.name}`}
                </span>
              ) : null}
              {added.map((row) => (
                <span
                  key={row.key}
                  data-testid={`plan-${plan.code}-${row.key}`}
                  style={{ display: 'flex', gap: 9, fontSize: 12.5 }}
                >
                  <Icon name="check" size={14} strokeWidth={2.2} />
                  {rowLabel(plan, row)}
                </span>
              ))}
              {missing.map((row) => (
                <span
                  key={row.key}
                  data-testid={`plan-${plan.code}-${row.key}`}
                  className="mm"
                  style={{ display: 'flex', gap: 9, fontSize: 12.5, opacity: 0.6 }}
                >
                  <span aria-hidden="true" style={{ width: 14, textAlign: 'center' }}>
                    —
                  </span>
                  {rowLabel(plan, row)}
                </span>
              ))}
            </span>
          </button>
        );
      })}
    </div>

    {/* THE TERM IS A SECOND QUESTION, and it only exists once a paid
        plan is on the table — a billing table beside Free would be
        asking about a purchase nobody is making. */}
    {selectedPlan !== null ? (
      <div
        className="card1"
        data-testid="plan-terms"
        style={{ padding: '22px 24px 24px', marginTop: 20 }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 16,
            flexWrap: 'wrap',
            alignItems: 'baseline',
          }}
        >
          <div>
            <h3 className="dh" style={{ fontSize: 19 }}>
              Billing period
            </h3>
            <p className="mm" style={{ fontSize: 12.5, margin: '6px 0 0' }}>
              Save more when you keep the project active for longer.
            </p>
          </div>
          <p className="mm" style={{ fontSize: 12.5, margin: 0 }}>
            Prices shown for <strong>{selectedPlan.name}</strong> plan
          </p>
        </div>

        <div className="npg-terms" style={{ marginTop: 16 }}>
          {selectedPlan.prices.map((price) => {
            const on = chosen?.term === price.termMonths;
            const saved = savingPercent(selectedPlan, price.termMonths);

            return (
              <button
                key={price.termMonths}
                type="button"
                data-testid={`plan-${selectedPlan.code}-term-${String(price.termMonths)}`}
                data-on={on ? '1' : '0'}
                aria-pressed={on}
                onClick={() => {
                  onChoose({ code: selectedPlan.code, term: price.termMonths });
                }}
                style={{
                  position: 'relative',
                  textAlign: 'left',
                  cursor: 'pointer',
                  padding: '16px 18px 18px',
                  background: on ? 'var(--color-surface)' : 'var(--color-bg)',
                  border: `1px solid ${on ? 'var(--color-accent)' : 'var(--color-divider)'}`,
                  boxShadow: on ? 'inset 0 0 0 1px var(--color-accent)' : 'none',
                }}
              >
                <span
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    paddingRight: 26,
                    flexWrap: 'wrap',
                  }}
                >
                  <span style={{ fontSize: 13.5, fontWeight: 600 }}>
                    {termLabel(price.termMonths)}
                  </span>
                  {saved === null ? null : (
                    <span
                      className="chip"
                      style={{
                        background: 'var(--color-accent)',
                        borderColor: 'var(--color-accent)',
                        color: '#fff',
                      }}
                    >
                      {`Save ${String(saved)}%`}
                    </span>
                  )}
                </span>
                <span
                  aria-hidden="true"
                  style={{
                    position: 'absolute',
                    top: 18,
                    right: 18,
                    width: 16,
                    height: 16,
                    borderRadius: '50%',
                    border: `1px solid ${on ? 'var(--color-accent)' : 'var(--color-divider)'}`,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {on ? (
                    <span
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        background: 'var(--color-accent)',
                      }}
                    />
                  ) : null}
                </span>
                <span className="dh" style={{ display: 'block', fontSize: 26, marginTop: 12 }}>
                  {money(price.amountMinor, price.currency)}
                </span>
                <span className="mm" style={{ display: 'block', fontSize: 12, marginTop: 6 }}>
                  {price.termMonths === 1
                    ? 'per month'
                    : `${money(price.monthlyEquivalentMinor, price.currency)} / month`}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    ) : null}

    {/* WHAT "PROJECT-BASED" MEANS WHEN THE SHOOT IS OVER — the question
        the pricing raises and the one nobody asks out loud: am I paying
        for this for ever. */}
    <div
      className="card1"
      data-testid="plan-lifecycle"
      style={{ padding: '22px 24px', marginTop: 20 }}
    >
      <h3 className="dh" style={{ fontSize: 17 }}>
        Pay only while your project is active
      </h3>
      <p className="mm" style={{ fontSize: 12.5, margin: '6px 0 0' }}>
        When production wraps, archive the project for free.
      </p>
      <div className="npg-steps" style={{ marginTop: 18 }}>
        {[
          {
            icon: 'play' as IconName,
            title: '1. Work',
            body: 'Choose a plan while the production is active.',
          },
          {
            icon: 'flag' as IconName,
            title: '2. Wrap',
            body: 'When the project is finished, archive it.',
          },
          {
            icon: 'archive' as IconName,
            title: '3. Archive — free',
            body: 'Your project and its history stay saved at no cost.',
          },
        ].map((s) => (
          <div key={s.title} style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 38,
                height: 38,
                flex: 'none',
                borderRadius: '50%',
                border: '1px solid var(--color-divider)',
              }}
            >
              <Icon name={s.icon} size={18} />
            </span>
            <span>
              <span className="flbl" style={{ display: 'block', margin: 0 }}>
                {s.title}
              </span>
              <span
                className="mm"
                style={{ display: 'block', fontSize: 12.5, marginTop: 5, lineHeight: 1.5 }}
              >
                {s.body}
              </span>
            </span>
          </div>
        ))}
        <p
          className="mm"
          style={{
            fontSize: 12.5,
            lineHeight: 1.55,
            margin: 0,
            paddingLeft: 18,
            borderLeft: '1px solid var(--color-divider)',
          }}
        >
          No monthly charges while a project is archived. Reactivate it anytime if
          production work resumes — nothing is ever deleted, and it opens where it left
          off.
        </p>
      </div>
    </div>
    </>
  );
}
